import { useSearchParams } from "react-router-dom";

/**
 * A list's filters, kept in the URL.
 *
 * In the URL so a filtered list can be linked to a colleague and survives a
 * reload. A new value replaces the last in the history rather than stacking
 * on it, and an empty one removes the parameter: one spelling of "unset".
 *
 * Several keys change in ONE update. Two calls in one event would each start
 * from the URL as it was, and the second would put back what the first took
 * out, so a filter that moves two keys at once -- a date range -- names both.
 */
export function useUrlParams<K extends string>(): [
  (key: K) => string,
  (next: Partial<Record<K, string>>) => void,
] {
  const [params, setParams] = useSearchParams();
  const get = (key: K) => params.get(key) ?? "";
  const set = (next: Partial<Record<K, string>>) =>
    setParams(
      (current) => {
        const updated = new URLSearchParams(current);
        for (const [key, value] of Object.entries<string | undefined>(next)) {
          if (value) updated.set(key, value);
          else updated.delete(key);
        }
        return updated;
      },
      { replace: true },
    );
  return [get, set];
}

/** One filter in the URL (`useUrlParams`). */
export function useUrlParam(key: string): [string, (next: string) => void] {
  const [get, set] = useUrlParams<string>();
  return [get(key), (next) => set({ [key]: next })];
}
